import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { api } from '~/lib/tuyau'
import Page from '../../components/Page'
import { withResourcePermission } from '../../components/RequireResourcePermission'

export const Route = createFileRoute('/_authenticated/cart-photos-x7k2qz')({
  component: withResourcePermission('carts', 'read')(CartPhotosPage),
})

const PAGE_SIZE = 50

function CartDetailDialog({ cartId, onClose }: { cartId: number; onClose: () => void }) {
  const { data: cart, isLoading: isCartLoading } = useQuery(
    api.carts.retrieveCart.queryOptions({ params: { id: cartId } }),
  )
  const { data: imagesData, isLoading: areImagesLoading } = useQuery(
    api.carts.getImages.queryOptions({ params: { id: cartId } }),
  )
  const images = imagesData?.images

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Panier #{cartId}</DialogTitle>
      <DialogContent>
        {isCartLoading ? (
          <CircularProgress />
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
              {cart?.articles.length ?? 0} article(s)
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Réf article</TableCell>
                  <TableCell>Produit</TableCell>
                  <TableCell>Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cart?.articles.map((article) => (
                  <TableRow key={article.checkpointArticleRef}>
                    <TableCell>{article.checkpointArticleRef}</TableCell>
                    <TableCell>{article.product?.label ?? article.productReference}</TableCell>
                    <TableCell>{article.soldStatus ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
          {areImagesLoading ? 'Chargement des photos…' : `${images?.length ?? 0} photo(s)`}
        </Typography>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {images?.map((image) => (
            <a key={image.key} href={image.url} target="_blank" rel="noreferrer">
              <img
                src={image.url}
                alt={image.key}
                loading="lazy"
                style={{ height: 240, borderRadius: 8, border: '1px solid #ddd' }}
              />
            </a>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function CartPhotosPage() {
  const [page, setPage] = useState(1)
  const [storeInput, setStoreInput] = useState('')
  const [selectedCartId, setSelectedCartId] = useState<number | null>(null)

  const store = storeInput ? Number(storeInput) : null
  const { data, isLoading } = useQuery(
    api.carts.listCartsWithImages.queryOptions({
      query: { page, pageSize: PAGE_SIZE, store: store ?? undefined },
    }),
  )

  const meta = data?.meta
  const rows = data?.data ?? []

  return (
    <Page>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Paniers avec photos
      </Typography>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <TextField
          size="small"
          label="Filtrer par store id"
          value={storeInput}
          onChange={(event) => {
            setStoreInput(event.target.value.replace(/\D/g, ''))
            setPage(1)
          }}
        />
        <Button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Précédent
        </Button>
        <Typography>
          Page {meta?.currentPage ?? page} / {meta?.lastPage ?? '?'} — {meta?.total ?? 0} panier(s)
        </Typography>
        <Button
          disabled={meta != null && meta.currentPage >= meta.lastPage}
          onClick={() => setPage((p) => p + 1)}
        >
          Suivant
        </Button>
      </div>

      <Paper>
        {isLoading ? (
          <CircularProgress sx={{ m: 3 }} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cart</TableCell>
                <TableCell>Magasin</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Créé</TableCell>
                <TableCell align="right">Articles</TableCell>
                <TableCell align="right">Photos</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((cart) => (
                <TableRow
                  key={cart.id}
                  hover
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedCartId(cart.id)}
                >
                  <TableCell>#{cart.id}</TableCell>
                  <TableCell>
                    {cart.storeLabel ?? '—'} (#{cart.storeId})
                  </TableCell>
                  <TableCell>{cart.status}</TableCell>
                  <TableCell>
                    {DateTime.fromISO(cart.createdAt).toFormat('dd/MM/yyyy HH:mm:ss')}
                  </TableCell>
                  <TableCell align="right">{cart.articleCount}</TableCell>
                  <TableCell align="right">{cart.imageCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {selectedCartId != null && (
        <CartDetailDialog cartId={selectedCartId} onClose={() => setSelectedCartId(null)} />
      )}
    </Page>
  )
}
